import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useContextSelector } from 'use-context-selector';
import { shouldSkipRefineReset } from './searchProductContext.guards';
import { DEFAULT_SEARCH_CATALOG_OPTION } from './searchProductContext.utils';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

const SearchProductOrchestration = () => {
  const { t } = useTranslation();

  const trimmedSearchTerm = useContextSelector(
    SearchProductQueryContext,
    (context) => context.trimmedSearchTerm
  );
  const isSearchProductsError = useContextSelector(
    SearchProductQueryContext,
    (context) => context.isSearchProductsError
  );

  const effectiveRefineSearch = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.effectiveRefineSearch
  );
  const priceRangeFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.priceRangeFilter
  );
  const itemFilterValue = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.itemFilter.value
  );
  const pendingFilterToastKey = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.pendingFilterToastKey
  );
  const clearPendingFilterToast = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.clearPendingFilterToast
  );
  const loadingProducts = useContextSelector(
    SearchProductQueryContext,
    (context) => context.loadingProducts
  );
  const catalogId = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.selectedCatalogFilter.catalogId
  );
  const setRefineSearch = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setRefineSearch
  );
  const setDataState = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setDataState
  );
  const setSelectedCatalogFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setSelectedCatalogFilter
  );

  const clearSelections = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.clearSelections
  );
  const handleCloseProductPanel = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.handleCloseProductPanel
  );
  const skipEmptyRefineResetRef = useRef(true);

  useEffect(() => {
    skipEmptyRefineResetRef.current = true;
    setRefineSearch('');
    setDataState((prev) => ({ ...prev, skip: 0 }));
    clearSelections();
    handleCloseProductPanel();
    setSelectedCatalogFilter(DEFAULT_SEARCH_CATALOG_OPTION);
  }, [
    trimmedSearchTerm,
    setRefineSearch,
    setDataState,
    clearSelections,
    handleCloseProductPanel,
    setSelectedCatalogFilter,
  ]);

  useEffect(() => {
    if (shouldSkipRefineReset(effectiveRefineSearch, skipEmptyRefineResetRef.current)) {
      skipEmptyRefineResetRef.current = false;
      return;
    }

    skipEmptyRefineResetRef.current = false;
    clearSelections();
    handleCloseProductPanel();
  }, [effectiveRefineSearch, clearSelections, handleCloseProductPanel]);

  useEffect(() => {
    setDataState((prev) => ({ ...prev, skip: 0 }));
  }, [priceRangeFilter, itemFilterValue, catalogId, setDataState]);

  useEffect(() => {
    if (isSearchProductsError) {
      toast.error(t('searchResultsLoadFailed'));
    }
  }, [isSearchProductsError, t]);

  useEffect(() => {
    if (!pendingFilterToastKey || loadingProducts) {
      return;
    }

    toast.info(t(pendingFilterToastKey), { autoClose: 3000 });
    clearPendingFilterToast();
  }, [pendingFilterToastKey, loadingProducts, t, clearPendingFilterToast]);

  return null;
};

export default SearchProductOrchestration;
